import { RESERVED_USERNAME_RE } from '@planuze/admin-contracts';
import { Button, InlineAlert, Input, Spinner, toast } from '@planuze/ui';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { useAccountsContent } from '../../content/sections/accounts.js';
import {
  useReservedUsernames,
  useUpdateReservedUsernames,
} from '../../hooks/use-reserved-usernames.js';
import { configServiceErrorMessage } from '../../lib/config-service-error.js';

/* Painel de gestão de @username reservados (ADR-0730, super_admin). Lista LIVE do config-service;
   add/remove faz PUT da lista inteira (replace). O auth-service une esta lista com um piso de
   segurança (handles estruturais como admin/api/root) que segue reservado mesmo se editada. */
export function ReservedUsernamesCard() {
  const copy = useAccountsContent().reservedUsernames;
  const { locale } = useAdminLocale();
  const { data, isLoading, isError } = useReservedUsernames();
  const update = useUpdateReservedUsernames();
  const [draft, setDraft] = useState('');

  const usernames = data ?? [];
  const busy = update.isPending;

  const commit = (next: readonly string[]): void => {
    update.mutate(
      { usernames: [...next] },
      {
        onError: (err) => toast.error(configServiceErrorMessage(err, copy.saveError, locale)),
      },
    );
  };

  const add = (): void => {
    const s = draft.trim().toLowerCase().replace(/^@+/, '');
    if (s.length === 0) return;
    if (!RESERVED_USERNAME_RE.test(s)) {
      toast.error(copy.invalidUsername);
      return;
    }
    if (usernames.includes(s)) {
      toast.error(copy.alreadyReserved);
      return;
    }
    commit([...usernames, s]);
    setDraft('');
  };

  const remove = (username: string): void => commit(usernames.filter((u) => u !== username));

  return (
    <section className="mb-4 flex flex-col gap-3 rounded-md border border-border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-base text-foreground">{copy.title}</h2>
        <p className="text-muted-foreground text-sm">{copy.description}</p>
      </div>

      {isError ? (
        <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
      ) : isLoading ? (
        <div className="grid place-items-center p-4">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {usernames.length === 0 ? (
            <span className="text-muted-foreground text-sm">{copy.empty}</span>
          ) : (
            usernames.map((username) => (
              <span
                key={username}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 font-mono text-foreground text-xs"
              >
                @{username}
                <button
                  type="button"
                  aria-label={copy.removeAriaLabel(username)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={busy}
                  onClick={() => remove(username)}
                >
                  <XIcon className="size-3" />
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <Input
          value={draft}
          placeholder={copy.newHandlePlaceholder}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          className="max-w-xs"
        />
        <Button type="submit" variant="outline" size="sm" loading={busy} disabled={busy}>
          {copy.add}
        </Button>
      </form>
    </section>
  );
}
